import { useState, type ReactNode } from 'react';
import { C, ink, surface } from '../../lib/theme';
import { ExpandButton, ErrorBanner, Skeleton } from '../primitives';
import { ScaleModal, type Origin } from '../ScaleModal';

export type Granularity = 'day' | 'week' | 'month';

/**
 * Chart Card (PRD §6 e §9): cada gráfico tem seletor de granularidade próprio no
 * canto (independente do filtro global) e ícone de expandir que abre o MESMO
 * gráfico em Scale Modal, sem perder o filtro ativo da tela.
 */
export function ChartCard({ id, title, granularity, onGranularity, loading, error, onRetry, expandedTitle, expandedWidth = 1040, expandedExtra, children, height = 260 }: {
  id?: string;
  title: string;
  granularity?: Granularity;
  onGranularity?: (g: Granularity) => void;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  expandedTitle?: string;
  /** Largura do Scale Modal ao expandir — default 1040 serve os gráficos de
   * série temporal (largos por natureza). Gráficos de conteúdo compacto
   * (rosca, calendário) passam um valor menor pra não abrir um modal
   * esticado com a maior parte vazia. */
  expandedWidth?: number;
  expandedExtra?: ReactNode;
  children: ReactNode;
  height?: number;
}) {
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <div id={id} style={{ ...surface, padding: 20, scrollMarginTop: 150 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{ minWidth: 0, fontSize: 14.5, fontWeight: 750, letterSpacing: '-0.01em' }}>{title}</div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
            {granularity && onGranularity && (
              <div style={{ display: 'flex', gap: 2, background: ink(0.06), borderRadius: 9, padding: 2 }}>
                {(['day', 'week', 'month'] as const).map((g) => (
                  <button key={g} type="button" onClick={() => onGranularity(g)} style={{
                    border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 10.5, fontWeight: 700,
                    padding: '4px 9px', borderRadius: 7,
                    background: granularity === g ? ink(0.13) : 'transparent',
                    color: granularity === g ? C.fg : ink(0.45),
                  }}>{g === 'day' ? 'Dia' : g === 'week' ? 'Sem' : 'Mês'}</button>
                ))}
              </div>
            )}
            <ExpandButton onClick={(e) => { setOrigin({ x: e.clientX, y: e.clientY }); setOpen(true); }} />
          </div>
        </div>

        {loading ? <Skeleton height={height} radius={14} />
          : error ? <ErrorBanner message={error} onRetry={onRetry} />
          : <div style={{ height }}>{children}</div>}
      </div>

      <ScaleModal
        open={open} origin={origin} onClose={() => setOpen(false)}
        title={expandedTitle ?? title} width={expandedWidth}
      >
        <div style={{ height: 440 }}>{children}</div>
        {expandedExtra && <div style={{ marginTop: 20 }}>{expandedExtra}</div>}
      </ScaleModal>
    </>
  );
}
